import { useEffect, useRef } from 'react';
import { announceCallbackComplete, subscribeToRefreshAuth } from '@customerio/hydra-nav-state';
import { getWorkspaceIdFromUrl } from '@/context/current-workspace';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

/**
 * Subscribes to `hydra:refresh-auth` and refetches auth data.
 *
 * The refetch runs immediately unless the URL carries a *different* workspace
 * id than the current workspace — that means a workspace switch is in flight
 * and the URL hasn't caught up, so the refetch is deferred until they match
 * (the useAuth hook reads the workspace id from the URL, so refetching early
 * would fetch stale data).
 *
 * Non-workspace URLs (e.g. /settings/billing after checkout) refetch
 * immediately: there is no URL to wait for, and the deferred flag lives in a
 * ref that a layout remount would lose, silently dropping the refresh.
 */
export function useRefreshAuthSubscription({
  refetch,
  currentWorkspaceId,
  locationPathname,
}: {
  refetch: () => Promise<unknown>;
  currentWorkspaceId: string | null;
  locationPathname: string;
}) {
  const currentWorkspaceIdRef = useRef(currentWorkspaceId);
  const shouldScheduleAuthRefresh = useRef(false);
  const refreshAuthCallbackId = useRef<string | null>(null);

  useMountEffect(() => {
    return subscribeToRefreshAuth(async (event) => {
      const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
      const workspaceId = currentWorkspaceIdRef.current;

      if (workspaceId != null && workspaceIdFromUrl != null && workspaceId !== workspaceIdFromUrl) {
        shouldScheduleAuthRefresh.current = true;
        refreshAuthCallbackId.current = event.detail.id;
      } else {
        try {
          await refetch();
        } finally {
          announceCallbackComplete(event.detail.id);
          refreshAuthCallbackId.current = null;
        }
      }
    });
  });

  useEffect(() => {
    currentWorkspaceIdRef.current = currentWorkspaceId;
  }, [currentWorkspaceId]);

  useEffect(() => {
    const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);

    // If there is an auth refresh scheduled and the workspace ids finally
    // match, run the deferred refetch.
    if (shouldScheduleAuthRefresh.current && currentWorkspaceIdRef.current === workspaceIdFromUrl) {
      shouldScheduleAuthRefresh.current = false;
      const callback = async () => {
        try {
          await refetch();
        } finally {
          if (refreshAuthCallbackId.current != null) {
            announceCallbackComplete(refreshAuthCallbackId.current);
            refreshAuthCallbackId.current = null;
          }
        }
      };
      void callback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationPathname, currentWorkspaceId]);
}
